import '../styles/ViewAthletes.css';

import React, {
  useEffect,
  useState,
} from 'react';

import {
  collection,
  getDocs,
  getFirestore,
  query,
} from 'firebase/firestore';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import firebase from '../firebase';
import { sortAthletesByNumber } from '../helper';
import { Athlete } from '../types/types';
import { checkSessionCodeAndLogoutIfChanged } from '../utils/sessionUtils';
import AthleteDetailPageFinalResult from './AthleteDetailPageFinalResult';
import {
  AthleteCardShimmer,
  Button,
  Card,
  Container,
  Input,
  Typography,
} from './common';
import AthleteCard from './common/AthleteCard';

export default function AthletePageFinalResult() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAthlete, setSelectedAthlete] = useState<Athlete | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [teamPlacementFilter, setTeamPlacementFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const db = getFirestore(firebase);
  const [showStickyName, setShowStickyName] = useState(false);

  useEffect(() => {
    checkSessionCodeAndLogoutIfChanged(localStorage.getItem('sessionId')!);

    async function fetchAthletes() {
      if (!sessionId) {
        setError('Session ID is missing');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');

        // Fetch athletes for this session
        const athletesQuery = query(collection(db, `sessions/${sessionId}/athletes`));
        const athletesSnapshot = await getDocs(athletesQuery);

        if (athletesSnapshot.empty) {
          setAthletes([]);
        } else {
          const athletesData = athletesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Athlete[];

          // Sort athletes by number in ascending order
          const sortedAthletes = sortAthletesByNumber(athletesData);
          setAthletes(sortedAthletes);
        }
      } catch (error) {
        console.error('Error fetching athletes:', error);
        setError('Failed to load athletes');
      } finally {
        setLoading(false);
      }
    }

    fetchAthletes();
  }, [sessionId, db]);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyName(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAthleteClick = (athlete: Athlete) => {
    setSelectedAthlete(athlete);
  };

  const handleCloseDetail = () => {
    setSelectedAthlete(null);
  };

  const handleNextAthlete = () => {
    if (!selectedAthlete) return;

    const currentIndex = athletes.findIndex(a => a.id === selectedAthlete.id);
    if (currentIndex < athletes.length - 1) {
      setSelectedAthlete(athletes[currentIndex + 1]);
    }
  };

  const handlePreviousAthlete = () => {
    if (!selectedAthlete) return;

    const currentIndex = athletes.findIndex(a => a.id === selectedAthlete.id);
    if (currentIndex > 0) {
      setSelectedAthlete(athletes[currentIndex - 1]);
    }
  };

  // First filter by position
  const positionFilteredAthletes = filter === 'all'
    ? athletes
    : athletes.filter(athlete => athlete.position === filter);

  // Then filter by team placement
  const teamPlacementFilteredAthletes = teamPlacementFilter === 'all'
    ? positionFilteredAthletes
    : teamPlacementFilter === 'TBD'
      ? positionFilteredAthletes.filter(athlete => !athlete.teamplacement || athlete.teamplacement === '')
      : positionFilteredAthletes.filter(athlete => athlete.teamplacement === teamPlacementFilter);

  // Apply search filter on top of position and team placement filters
  const searchFilteredAthletes = searchQuery
    ? teamPlacementFilteredAthletes.filter(athlete =>
      athlete.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      athlete.number.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : teamPlacementFilteredAthletes;

  // Add a function to calculate team placement counts
  const getTeamPlacementCounts = () => {
    const counts = {
      coed: 0,
      allgirl: 0,
      level4: 0,
      yes: 0,
      maybe: 0,
      no: 0,
      tbd: 0,
      unknown: 0
    };

    athletes.forEach(athlete => {
      if (athlete.teamplacement === 'coed') {
        counts.coed++;
      } else if (athlete.teamplacement === 'allgirl') {
        counts.allgirl++;
      } else if (athlete.teamplacement === 'level4') {
        counts.level4++;
      } else if (athlete.teamplacement === 'yes') {
        counts.yes++;
      } else if (athlete.teamplacement === 'maybe') {
        counts.maybe++;
      } else if (athlete.teamplacement === 'no') {
        counts.no++;
      } else if (!athlete.teamplacement || athlete.teamplacement === '' || athlete.teamplacement === 'TBD') {
        counts.tbd++;
      } else {
        counts.unknown++;
      }
    });

    return counts;
  };

  const teamPlacementCounts = getTeamPlacementCounts();
  const [summaryOpen, setSummaryOpen] = useState(true);

  if (!sessionId) {
    return (
      <Container>
        <Card>
          <Typography variant="h2" align="center">Error</Typography>
          <div className="error-message">Session ID is missing</div>
          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate('/join')}
          >
            Join a Session
          </Button>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <div className="new-athlete-top-bar">
          <div className="new-athlete-top-bar-left">
            <button className="new-athlete-top-bar-button" onClick={() => {
              if (selectedAthlete) {
                setSelectedAthlete(null);
              } else {
                navigate(`/session/${sessionId}`);
              }
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor" />
              </svg>
            </button>
          </div>
          <div className="new-athlete-top-bar-right"></div>
        </div>
        <div className="athlete-finalresultpage-content">
          <Card>
            <Typography variant="h2">Athlete Results</Typography>

            <div className="search-bar">
              <Input
                value=""
                onChange={() => { }}
                placeholder="Search"
                className="disabled-input"
              />
            </div>

            <Typography variant="body" className="filter-section-title">Position</Typography>
            <div className="filter-controls">
              <Button variant="secondary" disabled compact>All</Button>
              <Button variant="secondary" disabled compact>Tops</Button>
              <Button variant="secondary" disabled compact>Bases</Button>
              <Button variant="secondary" disabled compact>Thirds</Button>
              <Button variant="secondary" disabled compact>Multi</Button>
              <Button variant="secondary" disabled compact>Unknown</Button>
            </div>

            <Typography variant="body" className="filter-section-title">Team Placement</Typography>
            <div className="filter-controls">
              <Button variant="secondary" disabled compact>All</Button>
              <Button variant="secondary" disabled compact>Co-Ed</Button>
              <Button variant="secondary" disabled compact>AllGirl</Button>
              <Button variant="secondary" disabled compact>Yes</Button>
              <Button variant="secondary" disabled compact>Maybe</Button>
              <Button variant="secondary" disabled compact>No</Button>
              <Button variant="secondary" disabled compact>TBD</Button>
            </div>

            <div className="athletes-grid">
              {Array(6).fill(0).map((_, index) => (
                <AthleteCardShimmer key={index} />
              ))}
            </div>

            <div className="actions">
              <div className="shimmer shimmer-text long"></div>
            </div>
          </Card>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div className="new-athlete-top-bar">
          <div className="new-athlete-top-bar-left">
            <button className="new-athlete-top-bar-button" onClick={() => {
              if (selectedAthlete) {
                setSelectedAthlete(null);
              } else {
                navigate(`/session/${sessionId}`);
              }
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor" />
              </svg>
            </button>
          </div>
          <div className="new-athlete-top-bar-right"></div>
        </div>
        <div className="athlete-finalresultpage-content">
          <Card>
            <Typography variant="h2" align="center">Error</Typography>
            <div className="error-message">{error}</div>
            <Button
              variant="primary"
              fullWidth
              onClick={() => navigate(`/session/${sessionId}`)}
            >
              Back
            </Button>
          </Card>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="new-athlete-top-bar">
        <div className="new-athlete-top-bar-left">
          <button className="new-athlete-top-bar-button" onClick={() => {
            if (selectedAthlete) {
              setSelectedAthlete(null);
            } else {
              navigate(`/session/${sessionId}`);
            }
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor" />
            </svg>
          </button>
        </div>
        <div className="new-athlete-top-bar-center">
          <span className={selectedAthlete ? 'sticky-visible' : 'sticky-hidden'}>
            {selectedAthlete ? `#${selectedAthlete.number} ${selectedAthlete.name}` : ''}
          </span>
        </div>
        <div className="new-athlete-top-bar-right">
        </div>
      </div>
      <div className='athlete-finalresultpage-content'>
        <Card>
          <Typography variant="h2">Athlete Results</Typography>
          <div className="athlete-results-header">
            <div className="search-bar">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search"
              />
              {searchQuery && (
                <button
                  className="search-clear-button"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>

            <Typography variant="body" className="filter-section-title">Position</Typography>
            <div className="filter-controls">
              <Button
                variant={filter === 'all' ? 'primary' : 'secondary'}
                onClick={() => setFilter('all')}
                compact
              >
                All
              </Button>
              <Button
                variant={filter === 'Top' ? 'primary' : 'secondary'}
                onClick={() => setFilter('Top')}
                compact
              >
                Tops
              </Button>
              <Button
                variant={filter === 'Base' ? 'primary' : 'secondary'}
                onClick={() => setFilter('Base')}
                compact
              >
                Bases
              </Button>
              <Button
                variant={filter === 'Third' ? 'primary' : 'secondary'}
                onClick={() => setFilter('Third')}
                compact
              >
                Thirds
              </Button>
              <Button
                variant={filter === 'Multi' ? 'primary' : 'secondary'}
                onClick={() => setFilter('Multi')}
                compact
              >
                Multi
              </Button>
              <Button
                variant={filter === 'Unknown' ? 'primary' : 'secondary'}
                onClick={() => setFilter('Unknown')}
                compact
              >
                Unknown
              </Button>
            </div>

            <Typography variant="body" className="filter-section-title">Team Placement</Typography>
            <div className="filter-controls">
              <Button
                variant={teamPlacementFilter === 'all' ? 'primary' : 'secondary'}
                onClick={() => setTeamPlacementFilter('all')}
                compact
              >
                All
              </Button>
              <Button
                variant={teamPlacementFilter === 'coed' ? 'primary' : 'secondary'}
                onClick={() => setTeamPlacementFilter('coed')}
                compact
              >
                COED
              </Button>
              <Button
                variant={teamPlacementFilter === 'allgirl' ? 'primary' : 'secondary'}
                onClick={() => setTeamPlacementFilter('allgirl')}
                compact
              >
                AG
              </Button>
              <Button
                variant={teamPlacementFilter === 'level4' ? 'primary' : 'secondary'}
                onClick={() => setTeamPlacementFilter('level4')}
                compact
              >
                L4
              </Button>
              <Button
                variant={teamPlacementFilter === 'yes' ? 'primary' : 'secondary'}
                onClick={() => setTeamPlacementFilter('yes')}
                compact
              >
                Yes?
              </Button>
              <Button
                variant={teamPlacementFilter === 'maybe' ? 'primary' : 'secondary'}
                onClick={() => setTeamPlacementFilter('maybe')}
                compact
              >
                Maybe
              </Button>
              <Button
                variant={teamPlacementFilter === 'no' ? 'primary' : 'secondary'}
                onClick={() => setTeamPlacementFilter('no')}
                compact
              >
                No
              </Button>
              <Button
                variant={teamPlacementFilter === 'TBD' ? 'primary' : 'secondary'}
                onClick={() => setTeamPlacementFilter('TBD')}
                compact
              >
                TBD
              </Button>
            </div>
          </div>

          <div className="team-placement-summary">
            <div
              className="team-placement-summary-header"
              onClick={() => setSummaryOpen((open) => !open)}
            >
              <div className="team-placement-summary-title">
                <Typography variant="h3">Team Placement Summary</Typography>
              </div>
              <span
                className={`team-placement-summary-arrow ${summaryOpen ? 'open' : 'closed'}`}
                aria-label={summaryOpen ? 'Collapse' : 'Expand'}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor" />
                </svg>
              </span>
            </div>
            {summaryOpen && (
              <div className="team-placement-counts">
                <div className="team-placement-count">
                  <div className="team-placement-indicator coed"></div>
                  <span className="team-count">{teamPlacementCounts.coed}</span>
                  <span className="team-label">COED</span>
                </div>
                <div className="team-placement-count">
                  <div className="team-placement-indicator allgirl"></div>
                  <span className="team-count">{teamPlacementCounts.allgirl}</span>
                  <span className="team-label">ALLGIRL</span>
                </div>
                <div className="team-placement-count">
                  <div className="team-placement-indicator allgirl"></div>
                  <span className="team-count">{teamPlacementCounts.level4}</span>
                  <span className="team-label">Level4</span>
                </div>
                <div className="team-placement-count">
                  <div className="team-placement-indicator yes"></div>
                  <span className="team-count">{teamPlacementCounts.yes}</span>
                  <span className="team-label">Yes?</span>
                </div>
                <div className="team-placement-count">
                  <div className="team-placement-indicator maybe"></div>
                  <span className="team-count">{teamPlacementCounts.maybe}</span>
                  <span className="team-label">Maybe</span>
                </div>
                <div className="team-placement-count">
                  <div className="team-placement-indicator no"></div>
                  <span className="team-count">{teamPlacementCounts.no}</span>
                  <span className="team-label">No</span>
                </div>
                <div className="team-placement-count">
                  <div className="team-placement-indicator tbd"></div>
                  <span className="team-count">{teamPlacementCounts.tbd}</span>
                  <span className="team-label">TBD</span>
                </div>
              </div>
            )}
          </div>

          {searchFilteredAthletes.length === 0 ? (
            <div className="no-athletes">
              <Typography variant="h3">No athletes found</Typography>
              <Typography>Try a different search or filter</Typography>
            </div>
          ) : (
            <div className="athletes-grid">
              {searchFilteredAthletes.map(athlete => (
                <AthleteCard
                  key={athlete.id}
                  id={athlete.id}
                  name={athlete.name}
                  number={athlete.number}
                  position={athlete.position}
                  returningstatus={athlete.returningstatus}
                  coach={athlete.createdBy}
                  teamplacement={athlete.teamplacement}
                  imageUrl={athlete.imageThumbnailUrl ?? athlete.imageUrl}
                  onClick={() => handleAthleteClick(athlete)}
                />
              ))}
            </div>
          )}

          <div className="actions">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => navigate(`/session/${sessionId}`)}
            >
              Back
            </Button>
          </div>
        </Card>

        {selectedAthlete && (
          <div className="athlete-detail-overlay">
            <div className="athlete-detail-content">
              <AthleteDetailPageFinalResult
                athlete={selectedAthlete}
                onClose={handleCloseDetail}
                onNext={handleNextAthlete}
                onPrevious={handlePreviousAthlete}
                hasNext={athletes.findIndex(a => a.id === selectedAthlete.id) < athletes.length - 1}
                hasPrevious={athletes.findIndex(a => a.id === selectedAthlete.id) > 0}
              />
            </div>
          </div>
        )}
      </div>
    </Container>
  );
} 